/**
 * Pre-import confirm dialog (B-457). Renders the FREE preflight result — scanned
 * vs vector badge, claim header, ~N line items, export cost, project match, and
 * an optional sketch toggle — then returns the chosen options on Proceed. The
 * actual (free) enqueue happens in the caller.
 */
import { useState } from 'react';

import type { PreflightResponse, Project } from '../../types';
import { getProjectDisplayName } from '../../lib/utils';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Pill } from './ui/pill';
import ProjectMatchPicker from './ProjectMatchPicker';

/** The user's decision returned from the dialog on Proceed. */
export interface PreImportDecision {
  projectId?: string; // chosen existing project (or undefined for new/none)
  createNew?: boolean; // user chose to create a new project
  enableSketch: boolean; // sketch toggle (only when sketch_available)
}

export interface PreImportDialogProps {
  preflight: PreflightResponse;
  filename: string;
  onProceed: (_decision: PreImportDecision) => void;
  onCancel: () => void;
}

/** Free preflight confirm dialog. */
export default function PreImportDialog({
  preflight,
  filename,
  onProceed,
  onCancel,
}: PreImportDialogProps) {
  const [chosen, setChosen] = useState<Project | null>(preflight.project_match.exact);
  const [createNew, setCreateNew] = useState(false);
  const [enableSketch, setEnableSketch] = useState(false);

  const h = preflight.header_metadata;
  const addr = h.property_address
    ? [h.property_address.street, h.property_address.city, h.property_address.state, h.property_address.zip]
        .filter(Boolean)
        .join(', ')
    : null;

  const claimParts: Array<[string, string | null]> = [
    ['Insured', h.insured_name],
    ['Claim', h.claim_number],
    ['Address', addr],
    ['Estimate', h.estimate_name],
    ['Price list', h.price_list],
  ];

  const itemsLabel =
    preflight.is_scanned && preflight.estimated_line_count === 0
      ? '~unknown items'
      : `~${preflight.estimated_line_count} line items`;

  // Free can mean two different things: (a) the estimate is at/under the free
  // line threshold, or (b) the user is a member/unmetered (free regardless of
  // size). Only show the "(≤ N lines)" reason when it's actually true — showing
  // it on a 500-line estimate that's free because the user is a member is
  // misleading (live-reported bug).
  const costLabel =
    preflight.credits_required > 0
      ? `Export will cost ${preflight.credits_required} credit(s)`
      : preflight.estimated_line_count <= preflight.free_line_threshold
        ? `Free export (≤ ${preflight.free_line_threshold} lines)`
        : 'Free export';

  const footer = (
    <>
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={() => onProceed({ projectId: chosen?.id, createNew, enableSketch })}
      >
        Proceed
      </Button>
    </>
  );

  return (
    <Modal
      label="Confirm import"
      title="Confirm import"
      subtitle={filename}
      onClose={onCancel}
      testId="pre-import-dialog"
      footer={footer}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill kind={preflight.is_scanned ? 'warn' : 'ok'}>
            {preflight.is_scanned ? 'Scanned (image)' : 'Vector (text)'}
          </Pill>
          {preflight.page_count > 0 && (
            <span className="text-[11px] text-[var(--tx-lo)]">
              {preflight.page_count} page{preflight.page_count === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <dl className="space-y-1">
          {claimParts
            .filter(([, v]) => Boolean(v))
            .map(([k, v]) => (
              <div key={k} className="flex gap-2 text-[12.5px]">
                <dt className="w-[88px] shrink-0 text-[var(--tx-lo)]">{k}</dt>
                <dd className="min-w-0 truncate text-[var(--tx-hi)]">{v}</dd>
              </div>
            ))}
        </dl>

        <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px]">
          <span className="font-[550] text-[var(--tx-hi)]">{itemsLabel}</span>
          <span className="text-[var(--tx-mid)]">{costLabel}</span>
        </div>

        <ProjectMatchPicker
          match={{
            exact_match: preflight.project_match.exact,
            fuzzy_matches: preflight.project_match.fuzzy,
          }}
          selectedProjectId={chosen?.id ?? null}
          createNewSelected={createNew}
          onSelect={(p) => {
            setChosen(p);
            setCreateNew(false);
          }}
          onCreateNew={() => {
            setChosen(null);
            setCreateNew(true);
          }}
        />

        {/* Reflect the picker selection so the user gets visible feedback —
            without this the "Create new project" click looked like a no-op. */}
        {(chosen || createNew) && (
          <p
            data-testid="pre-import-selection"
            className="text-[12px] font-[550] text-[var(--ok,var(--tx-hi))]"
          >
            {chosen
              ? `✓ Importing into ${getProjectDisplayName(chosen)}`
              : h.insured_name
                ? `✓ New project from "${h.insured_name}"`
                : '✓ A new project will be created from this estimate'}
          </p>
        )}

        {preflight.sketch_available && (
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
              <input
                type="checkbox"
                checked={enableSketch}
                onChange={(e) => setEnableSketch(e.target.checked)}
              />
              Generate sketch from this estimate
            </label>
            {/* S-B4: the sketch is read off the plan page, and Xactimate's PDF export
                leaves that page out unless it is ticked. Without it we can read each
                room's shape but not where the rooms sit, so the user has to know to
                include it before they export, not after. */}
            <p className="pl-6 text-[11.5px] text-[var(--tx-dim)]">
              For the best sketch, include the floor plan page when you export the PDF
              from Xactimate.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
